// frontend/src/App.js

import React, { useState, useEffect, useMemo } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Link,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ReactSortable } from "react-sortablejs";
import api from "./api";

import WelcomeAuthPage from "./WelcomeAuthPage";
import ResetPasswordPage from "./ResetPasswordPage";
import { getUser, getToken, clearUser, isAdmin } from "./auth";
import AdminUsers from "./AdminUsers";
import ProductionLoss from "./ProductionLoss";

import {
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
} from "recharts";

import PmSchedule from "./PmSchedule";
import DataPage from "./DataPage.js";

import {
  Home,
  Factory,
  Users,
  BookOpen,
  Calendar,
  FilePlus,
  ClipboardList,
  Settings,
  Wrench,
  Database,
  Archive,
  Trash2,
  ShieldCheck,
} from "lucide-react";

import "./App.css";

import AddWorkOrder from "./AddWorkOrder";
import AddAsset from "./AddAsset";
import AssetsList from "./AssetsList";
import TeamList from "./TeamList";
import AddTechnician from "./AddTechnician";
import ContactsList from "./ContactsList";
import AddContact from "./AddContact";
import Scheduler from "./Scheduler";
import PartsList from "./PartsList";
import AddPart from "./AddPart";
import RequestsList from "./RequestsList";
import RequestForm from "./RequestForm";
import WorkOrdersList from "./WorkOrdersList";
import EditWorkOrder from "./EditWorkOrder";
import AllAssets from "./AllAssets";

function toDateSafe(v) {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

function isSameMonthYear(d, now) {
  return (
    d &&
    now &&
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth()
  );
}

function daysUntil(d, now) {
  if (!d || !now) return Number.POSITIVE_INFINITY;
  const ms = d.getTime() - now.getTime();
  return Math.floor(ms / (1000 * 60 * 60 * 24));
}

function normalizeStatus(status) {
  return String(status || "").toLowerCase().trim();
}

function isClosedWO(wo) {
  const s = normalizeStatus(wo.status);
  return s === "closed" || s === "completed" || s === "complete";
}

function ProtectedRoute({ children }) {
  const user = getUser();
  const token = getToken();
  const loc = useLocation();

  if (!user || !token) {
    return <Navigate to="/welcome" replace state={{ from: loc.pathname }} />;
  }

  return children;
}

function AdminRoute({ children }) {
  const user = getUser();
  const token = getToken();
  const loc = useLocation();

  if (!user || !token) {
    return <Navigate to="/welcome" replace state={{ from: loc.pathname }} />;
  }

  if (!isAdmin()) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function AppShell() {
  const loc = useLocation();
  const hideSidebar =
    loc.pathname === "/welcome" || loc.pathname.startsWith("/reset-password");

  return (
    <div className="app-container">
      {!hideSidebar ? <Sidebar /> : null}

      <div style={{ flex: 1 }}>
        <Routes>
          <Route path="/welcome" element={<WelcomeAuthPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          <Route
            path="/admin-users"
            element={
              <AdminRoute>
                <AdminUsers />
              </AdminRoute>
            }
          />

          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/assets" element={<ProtectedRoute><AssetsList /></ProtectedRoute>} />
          <Route path="/add-asset" element={<ProtectedRoute><AddAsset /></ProtectedRoute>} />
          <Route path="/team" element={<ProtectedRoute><TeamList /></ProtectedRoute>} />
          <Route path="/add-technician" element={<ProtectedRoute><AddTechnician /></ProtectedRoute>} />
          <Route path="/contacts" element={<ProtectedRoute><ContactsList /></ProtectedRoute>} />
          <Route path="/add-contact" element={<ProtectedRoute><AddContact /></ProtectedRoute>} />
          <Route path="/scheduler" element={<ProtectedRoute><Scheduler /></ProtectedRoute>} />
          <Route path="/add-work-order" element={<ProtectedRoute><AddWorkOrder /></ProtectedRoute>} />
          <Route path="/edit-work-order/:id" element={<ProtectedRoute><EditWorkOrder /></ProtectedRoute>} />
          <Route path="/parts" element={<ProtectedRoute><PartsList /></ProtectedRoute>} />
          <Route path="/add-part" element={<ProtectedRoute><AddPart /></ProtectedRoute>} />
          <Route path="/requests" element={<ProtectedRoute><RequestsList /></ProtectedRoute>} />
          <Route path="/new-request" element={<ProtectedRoute><RequestForm /></ProtectedRoute>} />
          <Route path="/work-orders" element={<ProtectedRoute><WorkOrdersList /></ProtectedRoute>} />
          <Route path="/all-assets" element={<ProtectedRoute><AllAssets /></ProtectedRoute>} />
          <Route path="/pm-schedule" element={<ProtectedRoute><PmSchedule /></ProtectedRoute>} />
          <Route path="/data" element={<ProtectedRoute><DataPage /></ProtectedRoute>} />
          <Route path="/production-loss" element={<ProtectedRoute><ProductionLoss /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </div>
    </div>
  );
}

function Sidebar() {
  const user = getUser();

  const [menuItems, setMenuItems] = useState([
    { label: "Dashboard", path: "/", icon: <Home size={18} /> },
    { label: "Team", path: "/team", icon: <Users size={18} /> },
    { label: "Contacts", path: "/contacts", icon: <BookOpen size={18} /> },
    { label: "Scheduler", path: "/scheduler", icon: <Calendar size={18} /> },
    { label: "WO Requests", path: "/requests", icon: <ClipboardList size={18} /> },
    { label: "Request WO", path: "/new-request", icon: <FilePlus size={18} /> },
    { label: "Parts", path: "/parts", icon: <Wrench size={18} /> },
    { label: "Work Orders List", path: "/work-orders", icon: <Settings size={18} /> },
    { label: "All Assets", path: "/all-assets", icon: <Factory size={18} /> },
    { label: "PM Schedule", path: "/pm-schedule", icon: <ClipboardList size={18} /> },
    { label: "Data", path: "/data", icon: <Database size={18} /> },
    { label: "Production Loss", path: "/production-loss", icon: <Database size={18} /> },
  ]);

  const logout = () => {
    clearUser();
    window.location.href = "/welcome";
  };

  return (
    <div className="sidebar">
      <div className="sidebar-header" style={{ alignItems: "center" }}>
        <div className="sidebar-logo">HM</div>
        <div className="sidebar-title">
          <span className="sidebar-title-main">HFAmaint</span>
          <span className="sidebar-title-sub">CMMS dashboard</span>
        </div>

        <button
          type="button"
          onClick={logout}
          className="btn-sm btn-purple"
          style={{ marginLeft: "auto" }}
        >
          Logout
        </button>
      </div>

      <ReactSortable
        list={menuItems}
        setList={setMenuItems}
        tag="ul"
        className="sidebar-nav"
      >
        {menuItems.map((item) => (
          <li key={item.path}>
            <Link to={item.path} className="sidebar-link">
              {item.icon}
              <span>{item.label}</span>
            </Link>
          </li>
        ))}
      </ReactSortable>

      {user?.role === "admin" && (
        <ul className="sidebar-nav" style={{ marginTop: 8 }}>
          <li>
            <Link to="/admin-users" className="sidebar-link">
              <ShieldCheck size={18} />
              <span>User Management</span>
            </Link>
          </li>
        </ul>
      )}
    </div>
  );
}

function Dashboard() {
  const [workOrders, setWorkOrders] = useState([]);
  const [pmSchedules, setPmSchedules] = useState([]);

  useEffect(() => {
    fetchWorkOrders();
    fetchPmSchedules();
  }, []);

  const fetchWorkOrders = () => {
    api
      .get("/api/work-orders")
      .then((res) => setWorkOrders(res.data))
      .catch((err) => console.error("Error fetching work orders", err));
  };

  const fetchPmSchedules = () => {
    api
      .get("/api/pm-schedules")
      .then((res) => {
        const data = res.data;
        const list = Array.isArray(data)
          ? data
          : data?.pm_schedules || data?.items || [];
        setPmSchedules(list);
      })
      .catch((err) => console.error("Error fetching PM schedules", err));
  };

  const deletePmSchedule = (id) => {
    if (!window.confirm("Delete this PM schedule?")) return;

    api
      .delete(`/api/pm-schedules/${id}`)
      .then(() => setPmSchedules((prev) => prev.filter((pm) => pm.id !== id)))
      .catch((err) => {
        console.error("Error deleting PM schedule", err);
        alert("Could not delete PM schedule.");
      });
  };

  const closePmSchedule = (id) => {
    if (!window.confirm("Close this PM schedule and create the next one?")) return;

    api
      .post(`/api/pm-schedules/${id}/close`)
      .then(() => fetchPmSchedules())
      .catch((err) => {
        console.error("Error closing PM schedule", err);
        alert("Could not close PM schedule.");
      });
  };

  const total = workOrders.length;
  const completed = workOrders.filter((wo) => isClosedWO(wo)).length;
  const openOrders = workOrders.filter((wo) => !isClosedWO(wo));
  const open = openOrders.length;
  const completionPct = total === 0 ? 0 : Math.round((completed / total) * 100);

  const donutData = [
    { name: "Completed", value: completed },
    { name: "Open", value: open },
  ];

  const typeCounts = openOrders.reduce((acc, wo) => {
    const t = wo.type || "Other";
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});

  const typeBarData = Object.entries(typeCounts).map(([type, count]) => ({
    type,
    count,
  }));

  const openByLine = openOrders.reduce((acc, wo) => {
    const line = wo.line || "Unknown";
    acc[line] = (acc[line] || 0) + 1;
    return acc;
  }, {});

  const lineBarData = Object.entries(openByLine).map(([line, count]) => ({
    line,
    count,
  }));

  const openByMachine = openOrders.reduce((acc, wo) => {
    const machine = wo.machine || "Unknown";
    acc[machine] = (acc[machine] || 0) + 1;
    return acc;
  }, {});

  const machineBarData = Object.entries(openByMachine)
    .map(([machine, count]) => ({ machine, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 10);

  const failureTypeByLineMap = openOrders.reduce((acc, wo) => {
    const line = wo.line || "Unknown";
    const type = wo.type || "Other";

    if (!acc[line]) {
      acc[line] = { line };
    }

    acc[line][type] = (acc[line][type] || 0) + 1;
    return acc;
  }, {});

  const failureTypeByLineData = Object.values(failureTypeByLineMap);
  const failureTypes = Array.from(
    new Set(openOrders.map((wo) => wo.type || "Other"))
  );

  const activePmSchedules = (pmSchedules || []).filter(
    (pm) => pm.is_active !== false
  );

  const pmAttentionList = useMemo(() => {
    const now = new Date();

    const decorated = activePmSchedules
      .map((pm) => {
        const d = toDateSafe(pm.date);
        const dueThisMonth = isSameMonthYear(d, now);
        const delta = daysUntil(d, now);

        const isOverdue = delta < 0;
        const isDueSoon = delta >= 0 && delta <= 7;

        return {
          pm,
          d,
          dueThisMonth,
          delta,
          isOverdue,
          isDueSoon,
        };
      })
      .filter(({ dueThisMonth, isDueSoon, isOverdue }) => {
        return dueThisMonth || isDueSoon || isOverdue;
      });

    decorated.sort((a, b) => {
      if (a.isOverdue !== b.isOverdue) return a.isOverdue ? -1 : 1;
      if (a.isDueSoon !== b.isDueSoon) return a.isDueSoon ? -1 : 1;
      if (a.dueThisMonth !== b.dueThisMonth) return a.dueThisMonth ? -1 : 1;

      const aTime = a.d ? a.d.getTime() : Number.POSITIVE_INFINITY;
      const bTime = b.d ? b.d.getTime() : Number.POSITIVE_INFINITY;

      return aTime - bTime;
    });

    return decorated;
  }, [activePmSchedules]);

  const riskByMachine = useMemo(() => {
    const now = new Date();
    const map = {};

    openOrders.forEach((wo) => {
      const machine = wo.machine || "Unknown";
      const line = wo.line || "Unknown";

      if (!map[machine]) {
        map[machine] = {
          machine,
          line,
          openWOs: 0,
          overduePMs: 0,
          dueSoonPMs: 0,
          riskScore: 0,
        };
      }

      map[machine].openWOs += 1;
      map[machine].riskScore += 2;
    });

    activePmSchedules.forEach((pm) => {
      const machine = pm.machine || "Unknown";
      const line = pm.line || "Unknown";
      const d = toDateSafe(pm.date);
      const delta = daysUntil(d, now);

      if (!map[machine]) {
        map[machine] = {
          machine,
          line,
          openWOs: 0,
          overduePMs: 0,
          dueSoonPMs: 0,
          riskScore: 0,
        };
      }

      if (delta < 0) {
        map[machine].overduePMs += 1;
        map[machine].riskScore += 5;
      } else if (delta >= 0 && delta <= 7) {
        map[machine].dueSoonPMs += 1;
        map[machine].riskScore += 3;
      }
    });

    return Object.values(map)
      .filter((x) => x.riskScore > 0)
      .sort((a, b) => b.riskScore - a.riskScore)
      .slice(0, 10);
  }, [openOrders, activePmSchedules]);

  const DONUT_COLORS = ["#22c55e", "#ef4444"];

  const PIE_COLORS = [
    "#38bdf8",
    "#6366f1",
    "#f97316",
    "#ef4444",
    "#22c55e",
    "#a855f7",
    "#14b8a6",
    "#f59e0b",
    "#ec4899",
    "#84cc16",
  ];

  const STACK_COLORS = ["#38bdf8", "#6366f1", "#f97316", "#ef4444", "#22c55e", "#a855f7", "#14b8a6"];

  const chartTooltipStyle = {
    background: "rgba(15, 23, 42, 0.96)",
    border: "1px solid rgba(148, 163, 184, 0.22)",
    borderRadius: 12,
    color: "#e5e7eb",
    boxShadow: "0 14px 35px rgba(0,0,0,0.25)",
  };

  const axisTickStyle = {
    fill: "#9ca3af",
    fontSize: 11,
  };

  const gridStroke = "rgba(148, 163, 184, 0.16)";

  const ChartCard = ({ title, subtitle, children }) => (
    <div className="card">
      <div style={{ marginBottom: 14 }}>
        <h3 style={{ margin: 0 }}>{title}</h3>
        {subtitle ? (
          <p style={{ margin: "6px 0 0", fontSize: 12, color: "#9ca3af" }}>
            {subtitle}
          </p>
        ) : null}
      </div>
      <div style={{ height: 290 }}>{children}</div>
    </div>
  );

  const CleanTooltip = () => (
    <Tooltip
      cursor={{ fill: "rgba(148, 163, 184, 0.08)" }}
      contentStyle={chartTooltipStyle}
      labelStyle={{ color: "#f9fafb", fontWeight: 700 }}
      itemStyle={{ color: "#e5e7eb" }}
    />
  );

  return (
    <div className="main-content">
      <style>{`
        @keyframes pmFlicker {
          0%, 100% { opacity: 1; transform: scale(1); filter: drop-shadow(0 0 0 rgba(239,68,68,0)); }
          50% { opacity: 0.35; transform: scale(1.05); filter: drop-shadow(0 0 10px rgba(239,68,68,0.55)); }
        }

        .pm-active-indicator {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
          font-size: 12px;
          padding: 6px 10px;
          border-radius: 999px;
          border: 1px solid rgba(148,163,184,0.25);
          background: rgba(255,255,255,0.03);
          white-space: nowrap;
        }

        .pm-indicator-dot {
          width: 10px;
          height: 10px;
          border-radius: 999px;
          display: inline-block;
        }

        .pm-dot-red {
          background: rgba(239,68,68,1);
          box-shadow: 0 0 0 4px rgba(239,68,68,0.15);
          animation: pmFlicker 1s infinite;
        }

        .pm-dot-orange {
          background: rgba(249,115,22,1);
          box-shadow: 0 0 0 4px rgba(249,115,22,0.15);
        }

        .pm-row-due {
          background: rgba(239,68,68,0.06);
        }

        .pm-row-soon {
          background: rgba(249,115,22,0.10);
        }

        .pm-date-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 6px 10px;
          border-radius: 10px;
          border: 1px solid rgba(148,163,184,0.25);
          background: rgba(255,255,255,0.03);
          font-weight: 600;
          font-size: 12px;
          white-space: nowrap;
        }

        .pm-date-badge.red {
          border-color: rgba(239,68,68,0.35);
          background: rgba(239,68,68,0.08);
        }

        .pm-date-badge.orange {
          border-color: rgba(249,115,22,0.35);
          background: rgba(249,115,22,0.08);
        }

        .summary-table td {
          font-size: 22px;
          font-weight: 800;
          padding: 12px;
        }

        .summary-table th {
          font-size: 12px;
          color: #9ca3af;
          padding: 10px;
        }
      `}</style>

      <h1>📊 Dashboard</h1>

      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>Work Orders Summary</h3>

        <table className="summary-table" style={{ width: "100%", textAlign: "center" }}>
          <thead>
            <tr>
              <th>Total</th>
              <th>Open</th>
              <th>Completed</th>
              <th>Completion %</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{total}</td>
              <td>{open}</td>
              <td>{completed}</td>
              <td>{completionPct}%</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <ChartCard title="Completion Overview" subtitle="Open vs completed work orders.">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={donutData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius={74}
                outerRadius={98}
                paddingAngle={4}
              >
                {donutData.map((entry, i) => (
                  <Cell key={`donut-${i}`} fill={DONUT_COLORS[i % DONUT_COLORS.length]} />
                ))}
              </Pie>
              <CleanTooltip />
              <Legend iconType="circle" />
            </PieChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Open Work Orders by Type" subtitle="Pie view of open work orders by type.">
          {typeBarData.length === 0 ? (
            <p style={{ color: "#9ca3af" }}>No open work orders yet.</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={typeBarData}
                  dataKey="count"
                  nameKey="type"
                  cx="50%"
                  cy="50%"
                  innerRadius={58}
                  outerRadius={96}
                  paddingAngle={3}
                >
                  {typeBarData.map((entry, i) => (
                    <Cell key={`type-pie-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <CleanTooltip />
                <Legend iconType="circle" />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 16 }}>
        <ChartCard title="Open Work Orders by Line" subtitle="Shows which production lines need the most attention.">
          {lineBarData.length === 0 ? (
            <p style={{ color: "#9ca3af" }}>No open work orders.</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={lineBarData} margin={{ top: 10, right: 18, left: 0, bottom: 10 }} barCategoryGap="30%">
                <CartesianGrid vertical={false} stroke={gridStroke} strokeDasharray="4 6" />
                <XAxis dataKey="line" tick={axisTickStyle} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={axisTickStyle} axisLine={false} tickLine={false} />
                <CleanTooltip />
                <Bar dataKey="count" fill="#6366f1" radius={[12, 12, 4, 4]} maxBarSize={44} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>

        <ChartCard title="Open Work Orders by Machine" subtitle="Pie view of top machines creating active workload.">
          {machineBarData.length === 0 ? (
            <p style={{ color: "#9ca3af" }}>No open work orders by machine.</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={machineBarData}
                  dataKey="count"
                  nameKey="machine"
                  cx="50%"
                  cy="50%"
                  innerRadius={58}
                  outerRadius={96}
                  paddingAngle={3}
                >
                  {machineBarData.map((entry, i) => (
                    <Cell key={`machine-pie-${i}`} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <CleanTooltip />
                <Legend iconType="circle" />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 16, marginTop: 16 }}>
        <ChartCard title="Failure Type by Line" subtitle="Stacked chart by line and work order type.">
          {failureTypeByLineData.length === 0 ? (
            <p style={{ color: "#9ca3af" }}>No data available.</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={failureTypeByLineData} margin={{ top: 10, right: 18, left: 0, bottom: 10 }} barCategoryGap="30%">
                <CartesianGrid vertical={false} stroke={gridStroke} strokeDasharray="4 6" />
                <XAxis dataKey="line" tick={axisTickStyle} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={axisTickStyle} axisLine={false} tickLine={false} />
                <CleanTooltip />
                <Legend iconType="circle" />
                {failureTypes.map((type, index) => (
                  <Bar
                    key={type}
                    dataKey={type}
                    stackId="a"
                    fill={STACK_COLORS[index % STACK_COLORS.length]}
                    radius={index === failureTypes.length - 1 ? [12, 12, 4, 4] : [0, 0, 0, 0]}
                    maxBarSize={46}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <h3 style={{ marginTop: 0 }}>PM Risk Score by Machine</h3>
        <p style={{ marginTop: -4, fontSize: 12, color: "#9ca3af" }}>
          Risk score = open WOs × 2 + due soon PMs × 3 + overdue PMs × 5.
        </p>

        <div style={{ height: 320 }}>
          {riskByMachine.length === 0 ? (
            <p style={{ color: "#9ca3af" }}>No PM risk detected.</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={riskByMachine} margin={{ top: 12, right: 22, left: 0, bottom: 10 }} barCategoryGap="25%">
                <CartesianGrid vertical={false} stroke={gridStroke} strokeDasharray="4 6" />
                <XAxis dataKey="machine" tick={axisTickStyle} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={axisTickStyle} axisLine={false} tickLine={false} />
                <CleanTooltip />
                <Legend iconType="circle" />
                <Bar dataKey="openWOs" stackId="risk" fill="#38bdf8" maxBarSize={46} />
                <Bar dataKey="dueSoonPMs" stackId="risk" fill="#f97316" maxBarSize={46} />
                <Bar dataKey="overduePMs" stackId="risk" fill="#ef4444" radius={[12, 12, 4, 4]} maxBarSize={46} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div className="card" style={{ marginTop: 16 }}>
        <div
          style={{
            display: "flex",
            gap: 12,
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 8,
          }}
        >
          <div>
            <h3 style={{ marginTop: 0, marginBottom: 6 }}>PM Attention Needed</h3>
            <p style={{ fontSize: 12, color: "#9ca3af", marginBottom: 0 }}>
              Showing only overdue, due soon, and due this month PM jobs.
            </p>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <span className="pm-active-indicator">
              <span className="pm-indicator-dot pm-dot-red" />
              Overdue / Due this month
            </span>

            <span className="pm-active-indicator">
              <span className="pm-indicator-dot pm-dot-orange" />
              Due soon
            </span>
          </div>
        </div>

        {pmAttentionList.length === 0 ? (
          <p style={{ fontSize: 13, color: "#9ca3af" }}>
            No PM jobs are overdue, due soon, or due this month ✅
          </p>
        ) : (
          <div className="table-wrapper">
            <div className="table-scroll">
              <table>
                <thead>
                  <tr>
                    <th>Job ID</th>
                    <th>Date</th>
                    <th>Line</th>
                    <th>Machine</th>
                    <th>Frequency</th>
                    <th>Assignee</th>
                    <th>Status</th>
                    <th style={{ width: 140 }}>Actions</th>
                  </tr>
                </thead>

                <tbody>
                  {pmAttentionList.map(({ pm, dueThisMonth, isOverdue, isDueSoon }) => {
                    const dotClass = isDueSoon ? "pm-dot-orange" : "pm-dot-red";

                    const activeLabel = isOverdue
                      ? "Overdue"
                      : isDueSoon
                      ? "Due soon"
                      : dueThisMonth
                      ? "Due this month"
                      : "Active";

                    const rowClass = isDueSoon ? "pm-row-soon" : "pm-row-due";
                    const badgeClass = isDueSoon ? "orange" : "red";

                    return (
                      <tr key={pm.id} className={rowClass}>
                        <td style={{ fontWeight: 700 }}>{pm.job_id}</td>

                        <td>
                          <span className={`pm-date-badge ${badgeClass}`}>
                            {pm.date}
                          </span>
                        </td>

                        <td>{pm.line}</td>
                        <td>{pm.machine}</td>
                        <td>
                          Every {pm.frequency} {pm.frequency_unit || "months"}
                        </td>
                        <td>{pm.assignee || "—"}</td>

                        <td>
                          <span className="pm-active-indicator" title={activeLabel}>
                            <span className={`pm-indicator-dot ${dotClass}`} />
                            {activeLabel}
                          </span>
                        </td>

                        <td>
                          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                            <button
                              type="button"
                              className="icon-btn danger"
                              onClick={() => deletePmSchedule(pm.id)}
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>

                            <button
                              type="button"
                              className="icon-btn ghost"
                              onClick={() => closePmSchedule(pm.id)}
                              title="Close"
                            >
                              <Archive size={16} />
                            </button>

                            <Link
                              to="/pm-schedule"
                              className="icon-btn"
                              title="Open PM Schedule page"
                              style={{
                                textDecoration: "none",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <ClipboardList size={16} />
                            </Link>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}
